<template>
<div class="chapter">
<div class="inner_page">
<div id="Chapter-Two.html" xml:lang="en-US">
<div class="Basic-Text-Frame-9">
<p class="ch"><a id="Anchor-1"></a>2</p>
<p class="sub"><em class="italic">San Francisco<br/>Wednesday, June 7,
						1978</em></p>
<p class="body-text-opening"><span class="caps-epub" xml:lang="en-US">Patty Takeda
						was </span>having the nightmare again.</p>
<p class="body-text">In it, she stood at the back of the church as the organist
					finished the last few measures of Franck’s “Fantaisie in C,” watching her maid
					of honor approach the altar and execute a perfect turn in her pink high heels.
					There was a pause as the entire congregation waited breathlessly. Then the first
					triumphant notes of the wedding march rang out, and everyone rose in their pews
					and turned toward the back, expectant smiles on their faces. Patty emerged from
					behind the latticed anteroom divider. Step–pause, step–pause, a smile fixed on
					her face.</p>
<p class="body-text">But something was wrong. Audible gasps filled the chapel and
					Patty looked down and discovered that she had forgotten to put her dress on. Or
					her slip, for that matter, or her panties or strapless bra. She was completely
					naked other than her white satin pumps. She tried to cover herself with her
					hands, but everyone was watching, staring, pointing, and she turned to run back
					to the dressing room but the ushers were standing shoulder to shoulder, blocking
					her way, gaping.</p>
<p class="body-text">Patty woke, shoulders heaving, sweat gluing her T-shirt to her
					neck, the sheets knotted around her body. She was breathing hard, but at least
					she was awake. Sometimes, when she had this dream, she ran around the church for
					what seemed like hours, never finding an exit.</p>
<p class="body-text">The sound of the doorbell jarred her fully awake. Was that the
					sound that had broken through the dream? Patty groped for the clock on the
					bedside table, knocking the tissue box to the floor before she found it. Almost
					nine. Patty lay still and listened as her mother answered the door. She heard
					her mother’s voice, and a man’s, back and forth a few times—and then footsteps,
					through the house, down the hall past Patty’s door, into the kitchen.</p>
<p class="body-text">“...can offer you tea, if you like, Inspector,” Patty heard her
					mother say clearly as they passed, and then the voices became indistinguishable
					again.</p>
<p class="body-text"><em class="italic">Inspector? </em>Patty untangled the
					sheets from her legs and sat up in bed, rubbing her face. Why would a detective
					be visiting her mother’s house? She pulled on the nylon running shorts she’d
					tossed on a chair the night before and was halfway to the door before she
					changed her mind and went back for her bra. It took a little searching—the bra
					had disappeared halfway under the bed—but Patty eventually found it and yanked
					it on, then exchanged the T-shirt she had been sleeping in for a fresh one from
					the suitcase on the floor. She sniffed under her armpits—not terrible. She
					really needed to unpack. She’d moved out of her apartment last week and she was
					staying here with her mother until the wedding, but it was only her third day
					off and she was still enjoying being lazy.</p>
<p class="body-text">She peeked out the bedroom door, craning her neck to peer into
					the kitchen, and saw a man’s polished brown shoe under the kitchen table. The
					rest of him was just out of sight. Patty grimaced and tiptoed across the hall to
					the bathroom. She washed her face and brushed her teeth in record time, pulling
					a comb through her hair and settling for a quick swipe of lip gloss.</p>
<p class="body-text">When she entered the kitchen, she was feeling presentable, if
					self-conscious about her bare legs. The man stood and greeted her with a
					nod.</p>
<p class="body-text">“Patty,” her mother said. “This is Inspector Torre.”</p>
<p class="body-text">“Pleased to meet you.”</p>
<p class="body-text">“You too,” Patty said automatically, taking the hand he
					offered, finding his grip surprisingly tentative. He was at least six, six-one,
					with the sort of beard that looks untended by lunchtime and thick, black
					sideburns encroaching on his jaw. Handsome, some women would no doubt think.</p>
<p class="body-text">“I’m here to talk to your mother about the death of an
					acquaintance of hers.”</p>
<p class="body-text">“Who?” Patty quickly cataloged everyone in her mother’s circle,
					a very short list. Besides work, Lucy Takeda went almost nowhere.</p>
<p class="body-text">“Reginald Forrest. He was the proprietor of a commercial gym in
					the basement of the DeSoto Hotel.”</p>
<p class="body-text">Patty knew the hotel—a once-grand stone edifice about a quarter
					mile away, on Pine or Bush or one of those streets. A pocket of the neighborhood
					that had seen the last of its glory days. But she had never heard the man’s
					name.</p>
<p class="body-text">Lucy <em class="italic">tsk</em>ed dismissively. “Someone I
					knew a long time ago, in Manzanar. I haven’t seen him in thirty-five years.”</p>
<p class="body-text">“But—” Patty looked from the inspector to her mother, confused.
					Lucy never spoke about her time in the internment camp. “Why on earth would you
					want to talk to my mother?”</p>
<p class="body-text">Torre cleared his throat, looking slightly uncomfortable.
					“Someone claims to have seen someone resembling your mother in the vicinity of
					the gym around the time he died. We’ve got a time of death between five and
					seven yesterday morning, and this person places your mother there between seven
					and seven-fifteen.”</p>
<p class="body-text">“But that’s—” Patty struggled to clear the morning haze from
					her thoughts. “My mom doesn’t ever go over there.”</p>
<p class="body-text">“This person said...” Inspector Torre seemed to be searching
					for the right words. “That is to say, he described certain characteristics....
					We asked around the neighborhood and several people mentioned Mrs. Takeda.”</p>
<p class="body-text">Now Patty understood his discomfort. “Characteristics...” Yes,
					people didn’t quickly forget her mother’s face. The pocked and shiny pink scars
					took up most of the right side of her face, extending from her right eye down to
					her jawline. They encroached upon her lower eyelid, pink and puffed and
					vertically clefted; the eye itself was milky and gave the impression of both
					blindness and acute vision, which was unsettling and put the observer in the
					uncomfortable position of having to find another place to focus his own
					eyes.</p>
<p class="body-text">“The inspector talked to Dave Navarro,” Lucy said indignantly.
					“And the Cooks!”</p>
<p class="body-text">The faint beginning of a headache stirred between Patty’s
					temples. Her mother had never had a great relationship with the neighbors—she
					could only imagine how those conversations went. “I’m sorry, but this is, well,
					I don’t get it,” Patty said. “I mean, you weren’t at the hotel yesterday
					morning, were you, Mom?”</p>
<p class="body-text">“Of course not. And besides, Inspector Torre said it could also
					be a suicide,” Lucy said. “It probably was.”</p>
<p class="body-text">“Why would you say that?” Torre asked.</p>
<p class="body-text">“<em class="italic">You</em> said that. You said the stun
					gun or whatever it was—”</p>
<p class="body-text">“Captive bolt pistol,” Inspector Torre said. “Often used with
					livestock, but it has other uses. What I meant was, was there something about
					Mr. Forrest that makes you think he might have been suicidal?”</p>
<p class="body-text">“How would I know?” Mrs. Takeda asked. “Reginald Forrest is an
					old man now. I’m sure he had his reasons.”</p>
<p class="body-text">“Was,” Torre interjected. “<em class="italic">Was</em> an
					old man.”</p>
<p class="body-text">Lucy shrugged. She was in an odd mood, both irritable and
					nervous, Patty thought. “Wait,” she said. “Can you just back up a little for me,
					Inspector? I’m sorry... I haven’t had my coffee. I’m not sure I’m following what
					you’re saying.”</p>
<p class="body-text">Lucy frowned, an expression that distorted her scars, and
					folded her arms over her chest.</p>
<p class="body-text">“Sure.” Torre reached for a notebook in his breast pocket,
					licked his thumb and started turning pages. “Janitor was buffing the lobby floor
					at about seven, seven-fifteen yesterday morning,” he said. “He described you
					pretty accurately. Said you appeared flustered, that you were walking faster
					than normal.”</p>
<p class="body-text">“He doesn’t know me,” Lucy said. “How does he know how fast I
					walk?”</p>
<p class="body-text">“<em class="italic">Mother</em>. Please.”</p>
<p class="body-text">“Your mother’s neighbors, Mr. David Navarro and Cindy and Tom
					Cook, did say that she takes frequent walks around the neighborhood.”</p>
<p class="body-text">“How would they know where I walk? They’re not my friends,”
					Lucy said. “They’ve never liked me. Dave Navarro had a tree whose roots were
					choking the sewage pipes under my house, and we argued over it until he finally
					cut it down. And the Cooks have a daughter who spreads her legs for every boy
					who comes around.”</p>
<p class="body-text">“Surely my mother isn’t the only person you’re interviewing,”
					Patty said hastily, painfully aware of how caustic Lucy could sound to someone
					who didn’t know her. She was a loner, but that certainly didn’t mean she’d
					killed anyone, a point Patty feared might be lost on Torre.</p>
<p class="body-text">He shrugged. “Sure, we’ve got a few people we’re talking to.
					Forrest had a son from a first marriage—he’s disturbed or retarded or something,
					lives in a group home. There’s also a girlfriend. I don’t suppose you can tell
					me anything about either of them.”</p>
<p class="body-text">“Of course not,” Lucy snapped. Patty tried to telegraph <em class="italic">be nice</em>. “I told you I haven’t talked to him in three
					decades.”</p>
<p class="body-text">“All right.” Torre tucked the notebook back in his pocket.
					“Here’s what I’m going to do. I’m going to give you a chance to think about
					Forrest, see if you remember anything that might help us out.”</p>
<p class="body-text">“From thirty years ago?” Damn, now she was doing it too—Patty
					instantly regretted snapping.</p>
<p class="body-text">Torre turned his gaze on her. “So you live here with your
					mother, Patty?”</p>
<p class="body-text">Patty resisted the urge to glare. “Only for a couple of weeks.
					I’m getting married. The wedding’s on the seventeenth.”</p>
<p class="body-text">“Oh. Well, in that case, congratulations.”</p>
<p class="body-text">He stood and adjusted his jacket, his eyes traveling up to the
					shelf that ran the length of the wall separating the kitchen from the dining
					room, and Patty cringed inwardly. This was the moment that marked every
					newcomer’s first visit to the house, the moment Patty had learned to dread so
					much that eventually she’d stopped bringing friends home at all.</p>
<p class="body-text">Patty let her gaze follow Torre’s, and tried to see what he
					saw, from his perspective—the gruesome tableau was as familiar to her as her
					mother’s Corelle dish pattern or the fake-brick design of the kitchen
					linoleum.</p>
<p class="body-text">All those eyes: wide and shiny, staring into every corner of
					the room at once. It probably seemed like there were dozens of them, but in
					reality there were only six or eight animals—squirrels and chipmunks and a pale
					little desert mouse, all of them stuffed and mounted so that they seemed to
					perch at the edge of the shelf, tiny claws curled around the edges of the
					painted board, hunching and crouching and tensed to jump, mouths open and
					leering, like so many gargoyles about to come to life.</p>
<p class="Basic-Paragraph-9"></p>
</div>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// ChapterTwo.vue?5cec619c